import { useRef } from 'react';
import { ModalDialog } from '@/components/ModalDialog/ModalDialog';
import { Checkbox } from '@alchemer/ui/components/checkbox';
import { tw } from '@/lib/tailwind';
import type { ShareDialogPayload } from '@/types';
import { isLiveboardSharePayload } from '@/types';
import { getObjectIdFromPayload } from '@/utils/thoughtspot';
import { ShareSearchBar } from './ShareSearchBar';
import { ShareInfo } from './ShareInfo';
import { ShareList } from './ShareList';
import { useShareDialog } from './useShareDialog';
import { useShareDialogSubmit } from './hooks/useShareDialogSubmit';

interface ShareDialogProps {
  open: boolean;
  payload: ShareDialogPayload | null;
  onClose: () => void;
}

function getShareUrl(payload: ShareDialogPayload | null): string {
  if (!payload) return window.location.href;
  const id = getObjectIdFromPayload(payload);
  if (!id) return window.location.href;
  return isLiveboardSharePayload(payload)
    ? `${window.location.origin}/dashboard/pinboard/${id}`
    : `${window.location.origin}/dashboard/charts/${id}`;
}

export function ShareDialog({ open, payload, onClose }: ShareDialogProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    isLoading,
    error,
    refetch,
    objectId,
    payload: activePayload,
    isDirty,
    sharedItems,
    availableItems,
    revokedUsers,
    searchSharedResults,
    searchResults,
    searchQuery,
    isSearchPending,
    setSearchQuery,
    addToShared,
    removeFromShared,
    updateItem,
    sendEmailNotification,
    setSendEmailNotification,
  } = useShareDialog(open, payload);

  const { submit, isPending } = useShareDialogSubmit(objectId);

  const shareUrl = getShareUrl(activePayload);

  return (
    <ModalDialog
      className={tw('sm:max-w-4xl')}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        searchInputRef.current?.focus();
      }}
      title="Share Content"
      showCloseButton
      actionBtnLabel="Save"
      actionBtnLoading={isPending}
      actionBtnVariant="default"
      disableActionButton={isPending || isLoading || !!error || !isDirty}
      actionBtnHandler={(closeModal) => {
        submit(sharedItems, revokedUsers, sendEmailNotification, closeModal);
      }}
      body={
        <>
          {/* TODO: Get feedback from Product and Design on copy */}
          <div className={tw('text-xs text-gray-600')}>Share this object</div>
          <ShareSearchBar
            inputRef={searchInputRef}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <ShareInfo
            sharedCount={sharedItems.length}
            shareUrl={shareUrl}
          />
          {error ? (
            <div className={tw('flex h-52 flex-col items-center justify-center', 'border-border gap-3 rounded border')}>
              <p className={tw('text-sm text-red-600')}>Failed to load users. Please try again.</p>
              <button
                type="button"
                className={tw('text-sm text-gray-600 underline hover:text-gray-900')}
                onClick={() => void refetch()}
              >
                Retry
              </button>
            </div>
          ) : (
            <ShareList
              isLoading={isLoading}
              sharedItems={sharedItems}
              availableItems={availableItems}
              searchSharedResults={searchSharedResults}
              searchResults={searchResults}
              searchQuery={searchQuery}
              isSearchPending={isSearchPending}
              onAdd={addToShared}
              onRemove={removeFromShared}
              onPermissionChange={(id, permission) => updateItem(id, { sharedAsPermission: permission })}
              onShareDataChange={(id, shareData) => updateItem(id, { shareUnderlyingData: shareData })}
            />
          )}
          <div className={tw('flex w-full justify-between gap-2')}>
            <div className={tw('text-xs text-gray-600')}>
              Note: If the user you are sharing with does not have the Editor option, they may need to login for the
              first time.
            </div>
            <div className={tw('flex items-center gap-2')}>
              <Checkbox
                id="sendEmailNotification"
                className={tw('border-gray-400')}
                checked={sendEmailNotification}
                onCheckedChange={(v) => setSendEmailNotification(v === true)}
              />
              <label
                htmlFor="sendEmailNotification"
                className={tw('text-sm text-gray-700')}
              >
                Send email notification
              </label>
            </div>
          </div>
        </>
      }
    />
  );
}
